'use client';

import { Suspense, lazy } from 'react';
import type { BarLineChartProps } from '@repo/ui/components/bar-line-chart';
import type { GradientChartProps } from '@repo/ui/components/gradient-chart';
import type { NestedDonutProps } from '@repo/ui/components/nested-donut';
import type { PieChartComponentProps } from '@repo/ui/components/pie-chart';
import { Skeleton } from '@repo/ui/components/skeleton';
import { cn } from '@repo/ui/lib/utils';

// Bundle-size: recharts (~610KB of route JS) reaches every chart route solely
// through these components. React.lazy moves it into an async chunk fetched
// after first paint; the Suspense fallback is a Skeleton matched to the
// chart's real rendered footprint — chart area plus the card padding and
// header chrome implied by the props — so nothing shifts on resolve. (The
// recharts legend renders absolutely *inside* the fixed-height ChartContainer,
// so it adds no external height and gets no allowance.)
// Consumers import { GradientChart } / { PieChartComponent } (and the
// others below) from here instead of @repo/ui — the props contract is
// identical (type-only imports from the source modules are erased at compile
// time).

const GradientChartImpl = lazy(() =>
  import('@repo/ui/components/gradient-chart').then((m) => ({
    default: m.GradientChart
  }))
);

const PieChartImpl = lazy(() =>
  import('@repo/ui/components/pie-chart').then((m) => ({
    default: m.PieChartComponent
  }))
);

const NestedDonutImpl = lazy(() =>
  import('@repo/ui/components/nested-donut').then((m) => ({
    default: m.NestedDonut
  }))
);

const BarLineChartImpl = lazy(() =>
  import('@repo/ui/components/bar-line-chart').then((m) => ({
    default: m.BarLineChart
  }))
);

// Chrome allowances (px), mirroring the source components' markup.
const CARD_PADDING = 50; // p-6 top+bottom + border
const HEADER_TITLE = 28; // text-xl heading line
const HEADER_DESCRIPTION = 24; // mt-1 text-sm supporting line

function ChartFallback({
  height,
  className
}: {
  height: number;
  className?: string;
}) {
  return <Skeleton className={cn('w-full', className)} style={{ height }} />;
}

export function GradientChart(props: GradientChartProps) {
  const { height = 400, noCard, title, description } = props;
  const hasHeader = Boolean(
    title || description || props.timelineOptions?.length
  );
  let fallbackHeight = height;
  if (!noCard) fallbackHeight += CARD_PADDING;
  if (hasHeader) {
    fallbackHeight += HEADER_TITLE + (noCard ? 16 : 32); // heading + mb-4/mb-8
    if (description) fallbackHeight += HEADER_DESCRIPTION;
  }

  return (
    <Suspense
      fallback={
        <ChartFallback height={fallbackHeight} className={props.className} />
      }
    >
      <GradientChartImpl {...props} />
    </Suspense>
  );
}

export function BarLineChart(props: BarLineChartProps) {
  // Chrome-less by design: the fallback is the chart area alone.
  return (
    <Suspense
      fallback={
        <ChartFallback
          height={props.height ?? 220}
          className={props.className}
        />
      }
    >
      <BarLineChartImpl {...props} />
    </Suspense>
  );
}

export function PieChartComponent(props: PieChartComponentProps) {
  const { height = 300, noCard, title } = props;
  let fallbackHeight = height;
  if (!noCard) fallbackHeight += CARD_PADDING;
  if (title) fallbackHeight += HEADER_TITLE + (noCard ? 16 : 24); // + mb-4/mb-6

  return (
    <Suspense
      fallback={
        <ChartFallback height={fallbackHeight} className={props.className} />
      }
    >
      <PieChartImpl {...props} />
    </Suspense>
  );
}

export function NestedDonut(props: NestedDonutProps) {
  // The fallback matches the ring's own footprint so nothing shifts when
  // the recharts chunk resolves.
  return (
    <Suspense
      fallback={
        <ChartFallback height={props.height ?? 260} className="rounded-full" />
      }
    >
      <NestedDonutImpl {...props} />
    </Suspense>
  );
}
